/**
 * DbSku - SKU 选择器组件
 *
 * 特性：
 * - 基于 DbPopup 实现底部弹窗
 * - 商品信息展示（图片 + 价格 + 编号）
 * - 使用 DbSelectionButton 进行属性选择（单选/多选）
 * - 使用 DbNumberInput 进行数量选择
 * - 使用 DbButton 进行底部操作
 * - 支持 SKU 组合联动（价格、库存、禁用）
 * - 支持自定义渲染
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { View, Text, Image, ScrollView, TouchableOpacity } from 'react-native';
import type {
    DbSkuProps,
    SkuAttribute,
    SkuSelectedResult,
    SkuGoodsInfo,
} from './DbSku.types';
import { createStyles } from './DbSku.styles';
import DbPopup from '../DbPopup';
import DbButton from '../DbButton';
import DbNumberInput from '../DbNumberInput';
import DbIcon from '../DbIcon';
import { DbSelectionButton } from '../DbSelectionButton';
import type { SelectionOption } from '../DbSelectionButton/DbSelectionButton.types';

export const DbSku: React.FC<DbSkuProps> = ({
    visible,
    onClose,
    // 商品信息
    goods,
    // SKU 属性
    attributes,
    defaultSelected = {},
    combinations,
    onAttributeChange,
    // 数量
    showQuantity = true,
    quantityTitle = '购买数量',
    defaultQuantity = 1,
    minQuantity = 1,
    maxQuantity = 99,
    onQuantityChange,
    // 操作按钮
    confirmText = '确定',
    onConfirm,
    addCartText = '加入购物车',
    onAddCart,
    showAddCart = false,
    // 自定义渲染
    renderGoodsInfo,
    renderFooter,
    renderExtra,
    renderAttribute,
    renderAttributes,
    selectionButtonConfig,
    // 样式
    popupStyle,
    goodsInfoStyle,
    priceStyle,
    attributeStyle,
    attributeTitleStyle,
    footerStyle,
}) => {
    const styles = useMemo(() => createStyles(), []);

    // 选中的属性值
    const [selectedAttrs, setSelectedAttrs] = useState<Record<string, string>>(
        defaultSelected
    );
    // 购买数量
    const [quantity, setQuantity] = useState(defaultQuantity);

    // 当 visible 变为 true 时重置状态（使用 ref 记录上次的 visible，避免 defaultSelected 引用变化导致无限循环）
    const prevVisibleRef = useRef(visible);
    useEffect(() => {
        // 只在 visible 从 false -> true 时重置
        if (visible && !prevVisibleRef.current) {
            setSelectedAttrs(defaultSelected);
            setQuantity(defaultQuantity);
        }
        prevVisibleRef.current = visible;
    }, [visible]);

    // 根据选中的属性查找匹配的 SKU 组合
    const matchedSku = useMemo(() => {
        if (!combinations || combinations.length === 0) return undefined;

        return combinations.find((combo) => {
            return Object.entries(selectedAttrs).every(
                ([attrId, valueId]) => combo.attributes[attrId] === valueId
            );
        });
    }, [combinations, selectedAttrs]);

    // 当前显示的价格（优先使用匹配 SKU 的价格）
    const displayPrice = useMemo(() => {
        if (matchedSku) return matchedSku.price;
        return goods.price;
    }, [matchedSku, goods.price]);

    // 当前显示的原价
    const displayOriginalPrice = useMemo(() => {
        if (matchedSku) return matchedSku.originalPrice;
        return goods.originalPrice;
    }, [matchedSku, goods.originalPrice]);

    // 当前显示的图片
    const displayImage = useMemo(() => {
        if (matchedSku?.imageUrl) return matchedSku.imageUrl;
        return goods.imageUrl;
    }, [matchedSku, goods.imageUrl]);

    // 当前可用的最大购买数量
    const currentMaxQuantity = useMemo(() => {
        if (matchedSku) return Math.min(matchedSku.stock, maxQuantity);
        return maxQuantity;
    }, [matchedSku, maxQuantity]);

    // 构建选择结果
    const buildResult = useCallback((): SkuSelectedResult => {
        return {
            selectedAttributes: { ...selectedAttrs },
            quantity,
            selectedSku: matchedSku,
        };
    }, [selectedAttrs, quantity, matchedSku]);

    // 处理属性选择变化
    const handleAttributeChange = useCallback(
        (attributeId: string, newValue: string | string[]) => {
            const valueId = Array.isArray(newValue) ? newValue[0] : newValue;
            const newSelected = { ...selectedAttrs };

            if (valueId === undefined || valueId === null) {
                delete newSelected[attributeId];
            } else {
                newSelected[attributeId] = valueId;
            }

            setSelectedAttrs(newSelected);
            onAttributeChange?.(attributeId, valueId, newSelected);
        },
        [selectedAttrs, onAttributeChange]
    );

    // 处理数量变化
    const handleQuantityChange = useCallback(
        (qty: number) => {
            setQuantity(qty);
            onQuantityChange?.(qty);
        },
        [onQuantityChange]
    );

    // 处理确认
    const handleConfirm = useCallback(() => {
        onConfirm?.(buildResult());
    }, [onConfirm, buildResult]);

    // 处理加入购物车
    const handleAddCart = useCallback(() => {
        onAddCart?.(buildResult());
    }, [onAddCart, buildResult]);

    // 格式化价格
    const formatPrice = (price: number) => {
        const parts = price.toFixed(2).split('.');
        return { integer: parts[0], decimal: parts[1] };
    };

    // 判断属性值是否因 SKU 组合而被禁用
    const isValueDisabledByCombination = useCallback(
        (attributeId: string, valueId: string): boolean => {
            if (!combinations || combinations.length === 0) return false;

            // 构造假选中状态
            const testSelected = { ...selectedAttrs, [attributeId]: valueId };

            // 检查是否有任何 SKU 组合包含此属性值且库存 > 0
            const hasValidCombo = combinations.some((combo) => {
                // 检查该组合是否与当前已选的其他属性匹配
                return (
                    Object.entries(testSelected).every(
                        ([attrId, val]) => combo.attributes[attrId] === val
                    ) && combo.stock > 0
                );
            });

            return !hasValidCombo;
        },
        [combinations, selectedAttrs]
    );

    // 将 SKU 属性转换为 DbSelectionButton 选项
    const convertToOptions = useCallback(
        (attribute: SkuAttribute): SelectionOption<string>[] => {
            return attribute.values.map((val) => ({
                value: val.id,
                label: val.name,
                disabled:
                    val.disabled ||
                    isValueDisabledByCombination(attribute.id, val.id),
            }));
        },
        [isValueDisabledByCombination]
    );

    // 渲染商品信息
    const renderDefaultGoodsInfo = () => {
        const currency = goods.currency || '¥';
        const { integer, decimal } = formatPrice(displayPrice);

        return (
            <View style={[styles.goodsInfoContainer, goodsInfoStyle]}>
                {/* 商品图片 */}
                <Image
                    source={
                        goods.imageSource ||
                        { uri: displayImage }
                    }
                    style={styles.goodsImage}
                    resizeMode="cover"
                />

                {/* 右侧信息 */}
                <View style={styles.goodsInfoRight}>
                    {/* 价格 */}
                    <View style={styles.priceRow}>
                        <Text style={[styles.priceCurrency, priceStyle]}>
                            {currency}
                        </Text>
                        <Text style={[styles.priceInteger, priceStyle]}>
                            {integer}
                        </Text>
                        <Text style={[styles.priceDecimal, priceStyle]}>
                            .{decimal}
                        </Text>
                        {displayOriginalPrice && (
                            <Text style={styles.originalPrice}>
                                {currency}
                                {displayOriginalPrice.toFixed(2)}
                            </Text>
                        )}
                    </View>

                    {/* 商品编号 */}
                    {goods.goodsCode && (
                        <Text style={styles.goodsCode}>
                            商品编号：{goods.goodsCode}
                        </Text>
                    )}

                    {/* 商品名称 */}
                    {goods.title && (
                        <Text style={styles.goodsTitle} numberOfLines={2}>
                            {goods.title}
                        </Text>
                    )}
                </View>
            </View>
        );
    };

    // 渲染单个属性（使用自定义渲染或默认渲染）
    const renderSingleAttribute = (attribute: SkuAttribute) => {
        const options = convertToOptions(attribute);
        const selectedValue = selectedAttrs[attribute.id];

        // 如果提供了自定义渲染函数，使用它
        if (renderAttribute) {
            return renderAttribute(
                attribute,
                options,
                selectedValue,
                (valueId) => handleAttributeChange(attribute.id, valueId)
            );
        }

        // 默认渲染
        return (
            <View
                key={attribute.id}
                style={[styles.attributeSection, attributeStyle]}
            >
                <Text style={[styles.attributeTitle, attributeTitleStyle]}>
                    {attribute.name}
                </Text>
                <DbSelectionButton<string>
                    mode={attribute.multiple ? 'multiple' : 'single'}
                    options={options}
                    value={selectedValue}
                    onChange={(val) =>
                        handleAttributeChange(attribute.id, val as string)
                    }
                    // 应用自定义配置
                    {...selectionButtonConfig}
                />
            </View>
        );
    };

    // 渲染属性选择区域
    const renderAttributeSection = () => {
        // 如果提供了完全自定义的 renderAttributes，使用它
        if (renderAttributes) {
            return renderAttributes(
                attributes,
                selectedAttrs,
                (attrId, valueId) => handleAttributeChange(attrId, valueId)
            );
        }

        // 默认渲染每个属性
        return attributes.map((attribute) => renderSingleAttribute(attribute));
    };

    // 渲染数量选择区域
    const renderQuantity = () => {
        if (!showQuantity) return null;

        return (
            <View style={styles.quantitySection}>
                <Text style={styles.quantityTitle}>{quantityTitle}</Text>
                <DbNumberInput
                    initialValue={quantity}
                    min={minQuantity}
                    max={currentMaxQuantity}
                    onChange={handleQuantityChange}
                />
            </View>
        );
    };

    // 渲染底部按钮
    const renderDefaultFooter = () => {
        if (renderFooter) {
            return renderFooter(buildResult());
        }

        return (
            <View style={[styles.footer, footerStyle]}>
                <View style={styles.footerButtons}>
                    {showAddCart && (
                        <DbButton
                            title={addCartText}
                            variant="outline"
                            size="large"
                            onPress={handleAddCart}
                            style={styles.addCartButton}
                        />
                    )}
                    <DbButton
                        title={confirmText}
                        variant="primary"
                        size="large"
                        onPress={handleConfirm}
                        style={styles.confirmButton}
                    />
                </View>
            </View>
        );
    };

    return (
        <DbPopup
            visible={visible}
            onClose={onClose}
            position="bottom"
            round
            closable
            closeIconPosition="top-right"
            style={popupStyle}
            safeAreaInsetBottom
        >
            <ScrollView
                showsVerticalScrollIndicator={false}
                bounces={false}
                keyboardShouldPersistTaps="handled"
            >
                {/* 商品信息 */}
                {renderGoodsInfo
                    ? renderGoodsInfo(goods)
                    : renderDefaultGoodsInfo()}

                {/* 属性选择 */}
                {renderAttributeSection()}

                {/* 额外内容 */}
                {renderExtra?.()}

                {/* 数量选择 */}
                {renderQuantity()}
            </ScrollView>

            {/* 底部操作按钮 */}
            {renderDefaultFooter()}
        </DbPopup>
    );
};

DbSku.displayName = 'DbSku';

export default DbSku;
export type {
    DbSkuProps,
    SkuAttribute,
    SkuAttributeValue,
    SkuCombination,
    SkuGoodsInfo,
    SkuSelectedResult,
    SelectionButtonConfig,
} from './DbSku.types';
